/**
 * The bar under a type's scenario list: the pick so far, and what to do with it. A bulk run or a new
 * dataset takes the pick; on a dataset's own type, picking cases for it, the pick joins that dataset.
 */

import type { ReactNode } from 'react';
import { Database, Loader2, Play, Plus } from 'lucide-react';
import { PillButton } from '@/modules/simulator/ui/pill-button';
import { Segmented } from '@/modules/simulator/ui/segmented';
import type { RunMode } from '@/types/websocket.types';
import { counted } from '../format';
import { MODE_OPTIONS } from '../runs/modes';
import { PanelNote } from './panel-note';

interface SelectionBarProps {
	selected: number;
	/** The scenarios a pick can take. */
	available: number;
	max: number;
	/** A warning about the pick, such as how a Live data run treats its fabricated scenarios. */
	note?: string | null;
	onSelectFirst: () => void;
	onClear: () => void;
	children: ReactNode;
}

export const SelectionBar = ({ selected, available, max, note, onSelectFirst, onClear, children }: SelectionBarProps) => (
	<div className='flex shrink-0 flex-col gap-1.5 border-t border-gray-500 px-3 py-2'>
		{note && <PanelNote>{note}</PanelNote>}
		{/* Wraps rather than squeezes: the actions cannot shrink, so the selection line takes a row
		    of its own when the SOP column leaves little room. */}
		<div className='flex flex-wrap items-center justify-between gap-x-3 gap-y-2'>
			{selected === 0 ? (
				<PillButton disabled={available === 0 || max === 0} onClick={onSelectFirst}>
					{available <= max ? `Select all ${available}` : `Select first ${max}`}
				</PillButton>
			) : (
				<div className='flex min-w-0 items-center gap-2'>
					<p className='text-xs leading-4 text-gray-800'>
						{selected} of {max} selected
					</p>
					<PillButton variant='ghost' onClick={onClear}>
						Clear
					</PillButton>
				</div>
			)}
			<div className='flex shrink-0 items-center gap-2'>{children}</div>
		</div>
	</div>
);

interface BulkRunActionsProps {
	selected: number;
	mode: RunMode;
	onModeChange: (mode: RunMode) => void;
	starting: boolean;
	onRun: () => void;
	onCreateDataset: () => void;
}

export const BulkRunActions = ({ selected, mode, onModeChange, starting, onRun, onCreateDataset }: BulkRunActionsProps) => (
	<>
		<PillButton disabled={selected === 0 || starting} onClick={onCreateDataset}>
			<Database className='size-3' />
			Create dataset
		</PillButton>
		<Segmented options={MODE_OPTIONS} value={mode} onChange={onModeChange} disabled={starting} />
		<PillButton variant='primary' disabled={selected === 0 || starting} onClick={onRun}>
			{starting ? <Loader2 className='size-3 animate-spin' /> : <Play className='size-3' />}
			Run{selected > 0 ? ` ${selected}` : ''}
		</PillButton>
	</>
);

export const AddCasesAction = ({ selected, adding, onAdd }: { selected: number; adding: boolean; onAdd: () => void }) => (
	<PillButton variant='primary' disabled={selected === 0 || adding} onClick={onAdd}>
		{adding ? <Loader2 className='size-3 animate-spin' /> : <Plus className='size-3' />}
		{selected > 0 ? `Add ${counted(selected, 'case')}` : 'Add cases'}
	</PillButton>
);
